<script setup lang="ts">
import { useRouter } from 'vue-router'

import { useCreditStore } from '../stores/creditStore'

import { useCalendlyBooking } from '@/shared/composables/useCalendlyBooking'
import { useEnv } from '@/shared/config'
import { useI18n } from '@/shared/i18n'
import { openIntercomChat } from '@/shared/lib/analytics/intercom'
import { Button, Icon, Modal, Skeleton } from '@/shared/ui'

withDefaults(defineProps<{ hideText?: boolean; selfServeEnabled?: boolean }>(), {
  hideText: false,
  selfServeEnabled: false,
})

const emit = defineEmits<{ selfServe: [] }>()
const store = useCreditStore()
const router = useRouter()
const { t } = useI18n()

function configuredBookCallUrl(): string | undefined {
  try {
    return useEnv().CREDIT_BOOK_CALL_URL
  } catch {
    return undefined
  }
}

const {
  isOpen: bookingOpen,
  loading: calendlyLoading,
  embedUrl: bookingUrl,
  open: openBooking,
  close: closeBooking,
  onFrameLoad,
} = useCalendlyBooking(configuredBookCallUrl)

function openBookCall(): void {
  openBooking()
  store.closeUpgrade()
}

function openChat(): void {
  if (openIntercomChat()) store.closeUpgrade()
}

function startSelfServe(): void {
  store.closeUpgrade()
  emit('selfServe')
  void router.push({ name: 'ComparePlans' })
}
</script>

<template>
  <Modal
    v-model="store.upgradeOpen"
    size="sm"
    width="28.125rem"
    :close-label="t('common.close')"
    @close="store.closeUpgrade"
  >
    <template v-if="!hideText" #title>
      <div class="credit-upgrade__title flex items-center gap-sm">
        <span
          class="credit-upgrade__title-icon inline-flex shrink-0 w-8 h-8 items-center justify-center rounded-md bg-orpheus-soft text-orpheus"
          aria-hidden="true"
        >
          <Icon name="bolt" :size="14" />
        </span>
        <span>{{ t('credits.billing.upgrade.title') }}</span>
      </div>
    </template>

    <section class="credit-upgrade grid gap-lg text-left" data-testid="credit-upgrade-modal">
      <p v-if="!hideText" class="credit-upgrade__description m-0 text-muted text-sm leading-normal">
        {{ t('credits.billing.upgrade.description') }}
      </p>

      <div class="credit-upgrade__actions grid gap-sm">
        <Button v-if="selfServeEnabled" block @click="startSelfServe">
          {{ t('credits.billing.upgrade.getStarted') }}
          <template #end><Icon name="arrow-right" :size="14" aria-hidden="true" /></template>
        </Button>

        <button
          type="button"
          class="credit-upgrade__option flex items-center gap-sm py-sm px-md border border-border rounded-lg bg-[none] text-inherit cursor-pointer [font:inherit] text-left transition-[border-color,background] duration-150 ease-[ease] [&:hover]:border-primary-subtle [&:hover]:bg-surface-sunken"
          @click="openBookCall"
        >
          <span
            class="credit-upgrade__option-icon credit-upgrade__option-icon--book inline-flex shrink-0 w-9 h-9 items-center justify-center rounded-md bg-orpheus-soft text-orpheus"
            aria-hidden="true"
          >
            <Icon name="calendar-check" :size="16" />
          </span>
          <span class="credit-upgrade__option-copy flex flex-1 flex-col gap-0.5 min-w-0">
            <span class="credit-upgrade__option-title text-fg text-[0.875rem] font-semibold">{{
              t('credits.billing.upgrade.bookCall')
            }}</span>
            <span class="credit-upgrade__option-subtitle text-muted text-[0.75rem]">{{
              t('credits.billing.upgrade.bookCallHint')
            }}</span>
          </span>
          <Icon
            name="external-link"
            :size="12"
            class="credit-upgrade__option-arrow shrink-0 text-muted"
            aria-hidden="true"
          />
        </button>

        <button
          type="button"
          class="credit-upgrade__option flex items-center gap-sm py-sm px-md border border-border rounded-lg bg-[none] text-inherit cursor-pointer [font:inherit] text-left transition-[border-color,background] duration-150 ease-[ease] [&:hover]:border-primary-subtle [&:hover]:bg-surface-sunken"
          @click="openChat"
        >
          <span
            class="credit-upgrade__option-icon credit-upgrade__option-icon--chat inline-flex shrink-0 w-9 h-9 items-center justify-center rounded-md bg-success-soft text-success"
            aria-hidden="true"
          >
            <Icon name="message-dot" :size="16" />
          </span>
          <span class="credit-upgrade__option-copy flex flex-1 flex-col gap-0.5 min-w-0">
            <span class="credit-upgrade__option-title text-fg text-[0.875rem] font-semibold">{{
              t('credits.billing.upgrade.chat')
            }}</span>
            <span class="credit-upgrade__option-subtitle text-muted text-[0.75rem]">{{
              t('credits.billing.upgrade.chatHint')
            }}</span>
          </span>
          <Icon
            name="arrow-right"
            :size="12"
            class="credit-upgrade__option-arrow shrink-0 text-muted"
            aria-hidden="true"
          />
        </button>
      </div>

      <p
        v-if="!hideText && !selfServeEnabled"
        class="credit-upgrade__notice flex items-center gap-xs m-0 py-xs px-sm rounded-md bg-surface-sunken text-muted text-[0.75rem]"
      >
        <Icon name="clock" :size="12" aria-hidden="true" />
        {{ t('credits.billing.upgrade.selfServeSoon') }}
      </p>
    </section>
  </Modal>

  <Modal
    v-model="bookingOpen"
    size="2xl"
    :title="t('credits.billing.upgrade.bookCall')"
    :close-label="t('common.close')"
    @close="closeBooking"
  >
    <section
      class="credit-booking relative min-h-[min(43.75rem,_70vh)]"
      data-testid="credit-booking-modal"
    >
      <Skeleton
        v-if="calendlyLoading"
        class="credit-booking__loading inset-0"
        width="100%"
        height="32rem"
        radius="var(--ui-radius-md)"
      />
      <iframe
        class="credit-booking__frame w-full h-[min(43.75rem,_70vh)] border-0 rounded-md"
        :class="{ 'credit-booking__frame--loading': calendlyLoading }"
        :src="bookingUrl"
        :title="t('credits.billing.upgrade.bookCall')"
        allow="camera; microphone; fullscreen"
        @load="onFrameLoad"
      />
    </section>
  </Modal>
</template>

<style scoped>
.credit-booking__frame--loading {
  visibility: hidden;
}
</style>
